import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileDown, Paperclip, Trash2, Pencil, Save, X, Plug, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import CommunicationThread from "@/components/CommunicationThread";

const EDITABLE_FIELDS = [
    "full_name", "email", "address_line1", "address_line2", "postal_code", "city", "country",
    "order_number", "order_date", "products", "reason", "iban",
];

export default function WithdrawalDetail() {
    const { id } = useParams();
    const { t } = useI18n();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [note, setNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [confirmDel, setConfirmDel] = useState(false);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [shopOrder, setShopOrder] = useState(null);
    const [fetchingOrder, setFetchingOrder] = useState(false);
    const [shopOrderError, setShopOrderError] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data: w } = await api.get(`/withdrawals/${id}`);
            setData(w);
            setNote(w.internal_note || "");
            try {
                const { data: s } = await api.get(`/shops/${w.shop_id}`);
                setShop(s);
            } catch {}
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    const startEdit = () => {
        const f = {};
        EDITABLE_FIELDS.forEach((k) => { f[k] = data[k] || ""; });
        setForm(f);
        setEditing(true);
    };
    const cancelEdit = () => { setEditing(false); setForm({}); };
    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const saveEdit = async () => {
        setSaving(true);
        try {
            const payload = {};
            EDITABLE_FIELDS.forEach((k) => {
                if (form[k] !== (data[k] || "")) payload[k] = form[k];
            });
            if (Object.keys(payload).length === 0) {
                setEditing(false);
                setSaving(false);
                return;
            }
            await api.put(`/withdrawals/${id}`, payload);
            toast.success(t("toast_changes_saved"));
            setEditing(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("toast_save_failed"));
        } finally { setSaving(false); }
    };

    const saveNote = async () => {
        setSavingNote(true);
        try {
            await api.put(`/withdrawals/${id}`, { internal_note: note });
            toast.success(t("toast_note_saved"));
            load();
        } catch (e) {
            toast.error(t("toast_note_failed"));
        } finally { setSavingNote(false); }
    };

    const fetchShopOrder = async () => {
        setFetchingOrder(true);
        setShopOrderError(null);
        try {
            const { data: order } = await api.get(`/withdrawals/${id}/shop-order`);
            setShopOrder(order);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setShopOrderError(detail === "no_integration" ? t("wd_shop_order_no_integration") : (detail || t("wd_shop_order_not_found")));
        } finally {
            setFetchingOrder(false);
        }
    };

    const changeStatus = async (val) => {
        if (val === "rejected") {
            setRejectReason("");
            setRejectDialog(true);
            return;
        }
        try {
            await api.put(`/withdrawals/${id}/status`, { status: val });
            toast.success(t("toast_status_updated"));
            load();
        } catch (e) {
            toast.error(t("toast_status_failed"));
        }
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) return;
        try {
            await api.put(`/withdrawals/${id}/status`, { status: "rejected", reason: rejectReason.trim() });
            toast.success(t("toast_status_updated"));
            setRejectDialog(false);
            load();
        } catch (e) {
            toast.error(t("toast_status_failed"));
        }
    };

    const downloadPdf = async () => {
        const token = localStorage.getItem("wr_token");
        const res = await fetch(`${API_BASE}/withdrawals/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `widerruf-${id.slice(0, 8)}.pdf`;
        a.click();
    };

    const downloadAttachment = async (idx, name) => {
        const token = localStorage.getItem("wr_token");
        const res = await fetch(`${API_BASE}/withdrawals/${id}/attachments/${idx}`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name || `anhang-${idx}`;
        a.click();
    };

    const remove = async () => {
        try {
            await api.delete(`/withdrawals/${id}`);
            toast.success(t("toast_wd_deleted"));
            nav("/app/withdrawals");
        } catch (e) { toast.error(t("toast_delete_failed")); }
    };

    if (loading) return <div className="text-sm text-gray-500">{t("loading")}</div>;
    if (!data) return <div className="text-sm text-gray-500">—</div>;

    const Row = ({ label, fieldKey, value, multi, type }) => (
        <div className="py-3 border-b border-gray-100 last:border-b-0">
            <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-1.5">{label}</div>
            {editing && fieldKey && EDITABLE_FIELDS.includes(fieldKey) ? (
                multi ? (
                    <Textarea
                        data-testid={`edit-${fieldKey}`}
                        rows={3}
                        value={form[fieldKey] || ""}
                        onChange={(e) => setField(fieldKey, e.target.value)}
                    />
                ) : (
                    <Input
                        data-testid={`edit-${fieldKey}`}
                        type={type || "text"}
                        value={form[fieldKey] || ""}
                        onChange={(e) => setField(fieldKey, e.target.value)}
                    />
                )
            ) : (
                <div className="text-sm text-[#00153f]">{value || "—"}</div>
            )}
        </div>
    );

    const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

    return (
        <div data-testid="withdrawal-detail">
            <Link to="/app/withdrawals" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f] mb-6">
                <ArrowLeft className="w-4 h-4" /> {t("back")}
            </Link>
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-1">
                        {t("wd_col_ref")} · {data.ref || data.id.slice(0, 8).toUpperCase()}
                    </div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{data.full_name}</h1>
                    <p className="text-gray-600 mt-1">{data.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {!editing ? (
                        <>
                            <Select value={data.status} onValueChange={changeStatus}>
                                <SelectTrigger data-testid="status-select" className="w-44"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">{t("status_new")}</SelectItem>
                                    <SelectItem value="in_progress">{t("status_in_progress")}</SelectItem>
                                    <SelectItem value="accepted">{t("status_accepted")}</SelectItem>
                                    <SelectItem value="rejected">{t("status_rejected")}</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button onClick={startEdit} data-testid="btn-edit" variant="outline" className="border-slate-300 text-[#00153f] hover:bg-[#00153f] hover:text-white hover:border-[#00153f]">
                                <Pencil className="w-4 h-4 mr-2" /> {t("edit")}
                            </Button>
                            <Button onClick={downloadPdf} data-testid="btn-pdf" className="bg-[#0b905d] hover:bg-[#067a4d] text-white">
                                <FileDown className="w-4 h-4 mr-2" /> {t("wd_pdf")}
                            </Button>
                            <Button variant="outline" onClick={() => setConfirmDel(true)} data-testid="btn-delete-wd" className="text-red-600 hover:text-red-700 border-red-200">
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={cancelEdit} variant="outline" data-testid="btn-cancel-edit" className="border-slate-300">
                                <X className="w-4 h-4 mr-2" /> {t("cancel")}
                            </Button>
                            <Button onClick={saveEdit} disabled={saving} data-testid="btn-save-edit" className="bg-[#0b905d] hover:bg-[#067a4d] text-white">
                                <Save className="w-4 h-4 mr-2" /> {saving ? "..." : t("save")}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 border border-gray-200 rounded-md bg-white p-6">
                    <h2 className="font-display text-lg font-semibold text-[#00153f] mb-2">{t("wd_section_consumer")}</h2>
                    <Row label="Name" fieldKey="full_name" value={data.full_name} />
                    <Row label={t("email")} fieldKey="email" value={data.email} type="email" />
                    <Row label={t("wd_field_address1")} fieldKey="address_line1" value={data.address_line1} />
                    <Row label={t("wd_field_address2")} fieldKey="address_line2" value={data.address_line2} />
                    <Row label={t("wd_field_zip")} fieldKey="postal_code" value={data.postal_code} />
                    <Row label={t("wd_field_city")} fieldKey="city" value={data.city} />
                    <Row label={t("wd_field_country")} fieldKey="country" value={data.country} />

                    <h2 className="font-display text-lg font-semibold text-[#00153f] mt-6 mb-2">{t("wd_section_order")}</h2>
                    <Row label={t("wd_field_order_no")} fieldKey="order_number" value={data.order_number} />
                    <Row label={t("wd_field_order_date")} fieldKey="order_date" value={data.order_date} type="date" />
                    <Row label={t("wd_field_products")} fieldKey="products" value={data.products} multi />
                    <Row label={t("wd_field_reason")} fieldKey="reason" value={data.reason} multi />
                    <Row label={t("wd_field_iban")} fieldKey="iban" value={data.iban} />

                    <h2 className="font-display text-lg font-semibold text-[#00153f] mt-6 mb-2">{t("wd_section_history")}</h2>
                    {(!data.history || data.history.length === 0) ? (
                        <div className="text-sm text-gray-500">{t("wd_history_empty")}</div>
                    ) : (
                        <ol className="relative border-l border-gray-200 ml-2 space-y-4 mt-2">
                            {[...data.history].reverse().map((ev, idx) => {
                                const at = (ev.at || "").slice(0, 19).replace("T", " ");
                                let label = ev.type;
                                let detail = null;
                                if (ev.type === "created") {
                                    label = t("wd_history_created_label");
                                    detail = t("wd_history_created_detail");
                                } else if (ev.type === "status_changed") {
                                    label = t("wd_history_status_changed");
                                    detail = `${t("status_" + ev.details?.from) || ev.details?.from} → ${t("status_" + ev.details?.to) || ev.details?.to}`;
                                } else if (ev.type === "updated") {
                                    const FLD_KEY = { full_name: "name", email: "email", address_line1: "wd_field_address1", address_line2: "wd_field_address2", postal_code: "wd_field_zip", city: "wd_field_city", country: "wd_field_country", order_number: "wd_field_order_no", order_date: "wd_field_order_date", products: "wd_field_products", reason: "wd_field_reason", iban: "wd_field_iban", internal_note: "wd_internal_note" };
                                    label = t("wd_history_data_changed");
                                    const items = Object.entries(ev.details || {});
                                    detail = (
                                        <ul className="space-y-0.5">
                                            {items.map(([fk, diff]) => (
                                                <li key={fk} className="text-xs">
                                                    <span className="font-medium text-[#00153f]">{FLD_KEY[fk] ? t(FLD_KEY[fk]) : fk}:</span>{" "}
                                                    <span className="text-gray-500 line-through">{String(diff.from ?? "—").slice(0, 50)}</span>
                                                    {" → "}
                                                    <span className="text-[#0b905d]">{String(diff.to ?? "—").slice(0, 50)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    );
                                }
                                return (
                                    <li key={idx} className="ml-4" data-testid={`history-${idx}`}>
                                        <span className="absolute -left-[5px] flex items-center justify-center w-2.5 h-2.5 rounded-full bg-[#0b905d] mt-1.5" />
                                        <div className="text-xs font-mono text-gray-500">{at}{ev.actor ? ` · ${ev.actor}` : ""}</div>
                                        <div className="text-sm font-medium text-[#00153f] mt-0.5">{label}</div>
                                        {detail && <div className="text-sm text-gray-600 mt-1">{detail}</div>}
                                    </li>
                                );
                            })}
                        </ol>
                    )}

                    <h2 className="font-display text-lg font-semibold text-[#00153f] mt-6 mb-2">{t("wd_attachments")}</h2>
                    {(!data.attachments || data.attachments.length === 0) ? (
                        <div className="text-sm text-gray-500">{t("wd_no_attachments")}</div>
                    ) : (
                        <ul className="space-y-2">
                            {data.attachments.map((a, idx) => (
                                <li key={idx} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Paperclip className="w-4 h-4 text-gray-500" />
                                        <span className="text-[#00153f]">{a.filename}</span>
                                        <span className="text-xs text-gray-500">({Math.round((a.size || 0) / 1024)} KB)</span>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => downloadAttachment(idx, a.filename)} data-testid={`att-dl-${idx}`}>
                                        <FileDown className="w-3.5 h-3.5" />
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <aside className="space-y-4">
                    {/* Shop System Order */}
                    <div className="border border-gray-200 rounded-md bg-white p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Plug className="w-4 h-4 text-gray-400" />
                                <span className="text-xs font-mono uppercase tracking-widest text-gray-500">{t("wd_shop_order_title")}</span>
                            </div>
                            <Button size="sm" variant="outline" onClick={fetchShopOrder} disabled={fetchingOrder} data-testid="btn-fetch-shop-order">
                                {fetchingOrder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("wd_fetch_order")}
                            </Button>
                        </div>
                        {shopOrderError && (
                            <p className="text-xs text-gray-500 italic">{shopOrderError}</p>
                        )}
                        {shopOrder && !shopOrderError && (
                            shopOrder.found === false ? (
                                <p className="text-xs text-gray-500 italic">{t("wd_shop_order_not_found")}</p>
                            ) : (
                                <div className="space-y-2 text-sm">
                                    {shopOrder.status && (
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">{t("wd_shop_order_status")}</span>
                                            <span className="font-mono text-xs text-[#00153f] font-medium">{shopOrder.status}</span>
                                        </div>
                                    )}
                                    {shopOrder.total && (
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">{t("wd_shop_order_total")}</span>
                                            <span className="font-mono text-xs text-[#00153f]">{shopOrder.total} {shopOrder.currency}</span>
                                        </div>
                                    )}
                                    {shopOrder.customer_name && (
                                        <div className="flex justify-between">
                                            <span className="text-xs text-gray-500">{t("wd_shop_order_customer")}</span>
                                            <span className="text-xs text-[#00153f]">{shopOrder.customer_name}</span>
                                        </div>
                                    )}
                                    {shopOrder.items && shopOrder.items.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <div className="text-xs text-gray-500 mb-2">{t("wd_shop_order_items")}</div>
                                            <ul className="space-y-1">
                                                {shopOrder.items.map((item, i) => (
                                                    <li key={i} className="flex justify-between text-xs">
                                                        <span className="text-[#00153f] truncate max-w-[60%]">{item.name}</span>
                                                        <span className="text-gray-500 font-mono">×{item.qty} · {item.price}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                        {!shopOrder && !shopOrderError && (
                            <p className="text-xs text-gray-400 italic">{t("wd_fetch_order")} →</p>
                        )}
                    </div>

                    {/* Internal Note */}
                    <div className="border border-gray-200 rounded-md bg-white p-5">
                        <div className="flex items-center justify-between mb-3">
                            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">{t("wd_internal_note")}</Label>
                        </div>
                        <Textarea
                            data-testid="internal-note"
                            rows={5}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={t("wd_internal_note_placeholder")}
                            className="resize-none"
                        />
                        <Button
                            size="sm"
                            onClick={saveNote}
                            disabled={savingNote || note === (data.internal_note || "")}
                            data-testid="btn-save-note"
                            className="mt-3 w-full bg-[#00153f] hover:bg-[#001a4d] text-white"
                        >
                            {savingNote ? "..." : t("wd_save_note")}
                        </Button>
                    </div>

                    <div className="border border-gray-200 rounded-md bg-white p-5">
                        <div className="text-xs font-mono uppercase tracking-widest text-gray-500">{t("wd_meta_received")}</div>
                        <div className="mt-1 text-sm text-[#00153f]">{fmtDate(data.created_at)}</div>
                        <div className="mt-4 text-xs font-mono uppercase tracking-widest text-gray-500">{t("wd_meta_updated")}</div>
                        <div className="mt-1 text-sm text-[#00153f]">{fmtDate(data.updated_at)}</div>
                        <div className="mt-4 text-xs font-mono uppercase tracking-widest text-gray-500">{t("wd_meta_lang")}</div>
                        <div className="mt-1 text-sm text-[#00153f]">{data.language?.toUpperCase()}</div>
                    </div>
                    {shop && (
                        <div className="border border-gray-200 rounded-md bg-white p-5">
                            <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">{t("wd_meta_shop")}</div>
                            <div className="flex items-center gap-3">
                                {shop.logo_url ? (
                                    <img src={shop.logo_url} alt="" className="h-10 w-10 rounded-md object-cover border border-gray-200" />
                                ) : (
                                    <div className="h-10 w-10 rounded-md flex items-center justify-center text-white font-display font-bold" style={{ background: shop.brand_color || "#00153f" }}>
                                        {shop.name.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div>
                                    <div className="font-semibold text-[#00153f]">{shop.name}</div>
                                    <div className="text-xs text-gray-500">/w/{shop.slug}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            <CommunicationThread withdrawalId={data.id} customerEmail={data.email} />

            <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("wd_delete_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("wd_delete_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">{t("delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Rejection reason dialog */}
            <AlertDialog open={rejectDialog} onOpenChange={(o) => { if (!o) setRejectDialog(false); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("wd_reject_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("wd_reject_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <Textarea
                        data-testid="reject-reason-input"
                        rows={4}
                        placeholder={t("wd_reject_reason_placeholder")}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="mt-2"
                    />
                    <AlertDialogFooter className="mt-4">
                        <AlertDialogCancel onClick={() => setRejectDialog(false)}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={!rejectReason.trim()}
                            onClick={confirmReject}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-reject-btn"
                        >
                            {t("status_rejected")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
